/**
 * useShotLatency Hook
 *
 * Fetches the latest shot latency data for a site on mount and polls every 5 seconds.
 * Returns { data, isLoading, isNoData, error }.
 *
 * Requirements: 1.1, 1.3, 6.1, 6.2, 6.3
 */

import { useState, useEffect, useRef } from 'react';
import { fetchShotLatency } from '../api/shotLatencyApiClient';
import { useAuth } from '../../auth/useAuth';
import { getAppConfig } from '../../config/appConfig';
import type { ShotLatencyResponse } from '../models/shotLatencyModels';

/** Polling interval in milliseconds */
export const POLL_INTERVAL_MS = 5_000;

export interface UseShotLatencyResult {
  readonly data: ShotLatencyResponse | null;
  readonly isLoading: boolean;
  readonly isNoData: boolean;
  readonly error: string | null;
}

export function useShotLatency(siteId: string | undefined): UseShotLatencyResult {
  const [data, setData] = useState<ShotLatencyResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNoData, setIsNoData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken } = useAuth();

  // Use a ref so the effect doesn't re-run when getAccessToken changes identity
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  useEffect(() => {
    if (!siteId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    let inFlight = false;

    async function load(isInitial: boolean) {
      if (inFlight) return;
      inFlight = true;

      if (isInitial) {
        setIsLoading(true);
      }

      try {
        const config = getAppConfig();
        const result = await fetchShotLatency(siteId!, {
          apiHost: config.apiHost,
          getAccessToken: getAccessTokenRef.current,
        });

        if (cancelled) return;

        switch (result.status) {
          case 'ok':
            setData(result.data);
            setIsNoData(false);
            setError(null);
            break;
          case 'no_data':
            setData(null);
            setIsNoData(true);
            setError(null);
            break;
          case 'error':
            setData(null);
            setIsNoData(false);
            setError(result.message);
            break;
        }
      } catch (err) {
        if (!cancelled) {
          setData(null);
          setIsNoData(false);
          setError(err instanceof Error ? err.message : 'Failed to load shot latency');
        }
      } finally {
        inFlight = false;
        if (!cancelled && isInitial) {
          setIsLoading(false);
        }
      }
    }

    // Fetch initially, then start polling after the first load completes
    load(true).then(() => {
      if (cancelled) return;
      const intervalId = setInterval(() => {
        load(false);
      }, POLL_INTERVAL_MS);

      // Store cleanup for the interval
      cleanupInterval = () => clearInterval(intervalId);
    });

    let cleanupInterval: (() => void) | undefined;

    return () => {
      cancelled = true;
      cleanupInterval?.();
    };
  }, [siteId]);

  return { data, isLoading, isNoData, error };
}
